/**
 * AddEditChallengeV2 — full V2 port of the Challenge edit form. Same
 * state shape, fetch graph, and submit handler as v1
 * (AddEditChallenges.js) so the API contract is byte-identical.
 *
 * Sections:
 *   01 Basic info        (title / goal / description / author)
 *   02 Schedule          (total_days + day_type)
 *   03 Intro session     (name / intro audio / intro image)
 *   04 Sharing & Media   (main + wide images + universal link)
 *   05 Notifications     (reminder / certificate / invite images)
 *   06 Sessions (edit-only) — sortable list with day-label input
 *   07 Danger zone (edit-only)
 *
 * Right rail:
 *   • PreviewCardCH      — challenge card preview
 *   • PublishingCardCH   — meta dl + Activate/Deactivate
 *   • SettingsCardCH     — 4 toggles
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { uploadImage, clearMedia, uploadAudio } from '../../../actions/mediaActions';
import {
  deleteChallenges,
  addEditChallenges,
} from '../../../actions/challengeActions';
import { fetchAuthors } from '../../../actions/authorsActions';
import { apiV2 } from '../../../actions/index';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  LinkField,
  UploadTile,
  AudioTile,
  AuthorSelectButton,
} from '../../redesign/edit';
import PublishingCardCH from './PublishingCardCH';
import SettingsCardCH from './SettingsCardCH';
import PreviewCardCH from './PreviewCardCH';
import ChallengeSessionsList from './ChallengeSessionsList';

const AddEditChallengeV2 = forwardRef(function AddEditChallengeV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── State (1:1 with v1) ─────────────────────────────────── */
  const [state, setState] = useState({
    challenge_title: '',
    challenge_description: '',
    challenge_goal: '',
    challenge_is_active: 1,
    challenge_image: '',
    challenge_image_preview: '',
    challenge_image_wide: '',
    challenge_image_wide_preview: '',
    challenge_day_type: '',
    total_days: 0,
    challenge_certificate_image: null,
    challenge_certificate_image_preview: '',
    challenge_invite_image: null,
    challenge_invite_image_preview: '',
    challenge_reminder_image: null,
    challenge_reminder_image_preview: '',
    challenge_is_dark_mode: 0,
    challenge_author_id: 0,
    challenge_is_free: 0,
    original_challenge_is_free: 0,
    intro_session: {
      session_name: '',
      session_main_url: '',
      session_main_url_preview: '',
      session_image: '',
      session_image_preview: '',
      session_length: '',
      session_length_in_sec: '',
      challenge_day: '',
    },
    sessions: [],
    session_data: [],
    isSetToEdit: false,
    challenge_is_new: 0,
    universal_link: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Stable handle for the parent's onLoaded callback. Read off a
  // ref inside the fetch effect so swapping the callback identity
  // doesn't kick off a duplicate fetch.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    dispatch(clearMedia());
    dispatch(fetchAuthors());

    if (isEdit) {
      apiV2.get(`/challenges/${selectedId}`).then((response) => {
        const currentChallenge = response.data;
        const session_data = currentChallenge.challenge_sessions || [];
        const sessions = session_data.map((obj) => {
          const row = {};
          row[obj.session_id] = [obj.challenge_day, obj.session_repetition];
          return row;
        });

        setState((prev) => ({
          ...prev,
          ...currentChallenge,
          challenge_is_new: currentChallenge.challenge_is_new ?? 0,
          original_challenge_is_free: currentChallenge.challenge_is_free,
          session_data,
          sessions,
          isSetToEdit: true,
          intro_session: currentChallenge.intro_session || {
            session_name: '',
            session_main_url: '',
            session_main_url_preview: '',
            session_image: '',
            session_image_preview: '',
            session_length: '',
            session_length_in_sec: '',
            challenge_day: '',
          },
        }));

        // Hand the page chrome just enough to render the sticky
        // header — title + updated_at — so it doesn't have to
        // fire its own parallel GET /challenges/{id}.
        if (onLoadedRef.current) {
          onLoadedRef.current({
            challenge_title: currentChallenge.challenge_title,
            challenge_updated_at:
              currentChallenge.challenge_updated_at
              ?? currentChallenge.updated_at
              ?? null,
          });
        }
      });
    }
  }, [dispatch, isEdit, selectedId]);

  /* ─── Field handlers ──────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  }, []);
  const handleIntroFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      intro_session: { ...prev.intro_session, [name]: value },
    }));
  }, []);

  /* ─── Image uploads (5 separate slots) ────────────────────── */
  //
  // Read the previous blob URL from inside the setState updater
  // (functional form) rather than from the closed-over `state`.
  // This drops `state` from the dep array so the callback's
  // identity is actually stable across renders — without that,
  // typing in any text field would recreate uploadAndSet and the
  // six handlers below it on every keystroke. The revokeObjectURL
  // call inside an updater is idempotent (revoking an
  // already-revoked URL is a no-op), so it's safe even if React
  // strict-mode invokes the updater twice.
  const uploadAndSet = useCallback(
    (file, previewField, urlField, isIntro = false) => {
      const url = window.URL.createObjectURL(file);
      setState((prev) => {
        const prevUrl = isIntro
          ? prev.intro_session[previewField]
          : prev[previewField];
        if (prevUrl) window.URL.revokeObjectURL(prevUrl);
        return isIntro
          ? { ...prev, intro_session: { ...prev.intro_session, [previewField]: url } }
          : { ...prev, [previewField]: url };
      });
      dispatch(uploadImage(file)).then((response) => {
        const remote = response.data.image_url;
        setState((prev) =>
          isIntro
            ? { ...prev, intro_session: { ...prev.intro_session, [urlField]: remote } }
            : { ...prev, [urlField]: remote },
        );
        dispatch(clearMedia());
      });
    },
    [dispatch],
  );

  const handleMainImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'challenge_image_preview', 'challenge_image');
  };
  const handleWideImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'challenge_image_wide_preview', 'challenge_image_wide');
  };
  const handleReminderImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'challenge_reminder_image_preview', 'challenge_reminder_image');
  };
  const handleCertificateImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'challenge_certificate_image_preview', 'challenge_certificate_image');
  };
  const handleInviteImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'challenge_invite_image_preview', 'challenge_invite_image');
  };
  const handleIntroImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadAndSet(f, 'session_image_preview', 'session_image', true);
  };

  /* ─── Intro audio upload — mirrors v1 ─────────────────────── */
  //
  // Same pattern as `uploadAndSet` above: read the previous blob URL
  // from inside the setState updater (functional form) instead of
  // closing over `state`. Keeps the dep array down to `[dispatch]`
  // so the callback identity is stable across renders. revokeObjectURL
  // is idempotent so it's safe even if strict-mode invokes the
  // updater twice.
  const handleIntroAudio = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const preview = window.URL.createObjectURL(file);
      setState((prev) => {
        if (prev.intro_session.session_main_url_preview) {
          window.URL.revokeObjectURL(prev.intro_session.session_main_url_preview);
        }
        return {
          ...prev,
          intro_session: { ...prev.intro_session, session_main_url_preview: preview },
        };
      });
      dispatch(uploadAudio(file)).then((response) => {
        setState((prev) => ({
          ...prev,
          intro_session: {
            ...prev.intro_session,
            session_main_url: response.data.audio_file_url,
          },
        }));
        dispatch(clearMedia());
      });
    },
    [dispatch],
  );

  // Derive intro length once the AudioTile signals onLoadedMetadata.
  const handleIntroAudioLoaded = useCallback((e) => {
    const sec = e?.target?.duration;
    if (!sec || Number.isNaN(sec)) return;
    const minutes = Math.floor(sec / 60);
    const seconds = Math.round(sec - minutes * 60);
    setState((prev) => ({
      ...prev,
      intro_session: {
        ...prev.intro_session,
        session_length: `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`,
        session_length_in_sec: Math.round(sec),
      },
    }));
  }, []);

  /* ─── Sessions (day label) ─────────────────────────────── */
  const handleChallengesDay = useCallback((e) => {
    const { name, value } = e.target;
    const id = name.replace(/\D/g, '');
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((obj) => {
        if (Object.keys(obj)[0] !== id) return obj;
        const values = Object.values(obj)[0];
        const next = {};
        if (name.includes('day')) {
          next[id] = [value, values[1]];
        } else {
          next[id] = [values[0], value];
        }
        return next;
      }),
    }));
  }, []);

  const handleSessionSort = useCallback(({ oldIndex, newIndex }) => {
    setState((prev) => {
      const next = [...prev.sessions];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      return { ...prev, sessions: next };
    });
  }, []);

  /* ─── Submit (1:1 with v1) ──────────────────────────────── */
  const submit = useCallback(() => {
    const errors = [];
    if (!state.challenge_title?.trim()) errors.push('Challenge title is required');
    if (!state.challenge_goal?.trim()) errors.push('Challenge goal is required');
    if (!state.total_days || !Number.isInteger(Number(state.total_days))) {
      errors.push('Total days must be a valid number');
    }
    if (!state.intro_session?.session_name?.trim()) {
      errors.push('Intro session name is required');
    }
    if (errors.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: errors.map((e) => `• ${e}`).join('<br/>'),
      });
      return;
    }

    setIsSaving(true);
    // Strip UI-only fields before serializing. The action thunk's
    // payload-builder destructures a fixed allow-list of fields,
    // so the extras would be silently dropped anyway — but
    // serializing them bloats the request body and (more
    // importantly) keeps the local-only blob: URLs out of the
    // wire format. If the API ever started persisting unknown
    // keys, sending these would corrupt data.
    const {
      sessions,                           // sent as session_ids_json
      intro_session,                      // sent as intro_session_json (cleaned below)
      session_data,                       // UI list state, never persisted
      isSetToEdit,                        // local "edit mode" flag
      universal_link,                     // derived display field
      challenge_image_preview,            // ↓ local blob: URLs
      challenge_image_wide_preview,
      challenge_certificate_image_preview,
      challenge_invite_image_preview,
      challenge_reminder_image_preview,
      ...challenge_json
    } = state;
    // `intro_session` carries its own local-only blob: preview URLs
    // (mirroring the top-level shape). Strip them before serializing
    // for the same reason as the top-level fields above.
    const {
      session_main_url_preview,
      session_image_preview,
      ...intro_session_clean
    } = intro_session || {};
    const options = {
      challenge_json: JSON.stringify(challenge_json),
      session_ids_json: JSON.stringify(sessions),
      intro_session_json: JSON.stringify(intro_session_clean),
    };
    // `addEditChallenges` doesn't return a promise from the inner
    // dispatch function. We branch on the runtime shape so the
    // timeout fallback only fires when the promise path isn't
    // available — without the if/else, the timeout always fires
    // alongside .finally and could prematurely dismiss the saving
    // state if the action is ever refactored to return a promise.
    const result = dispatch(addEditChallenges(options, () => navigate('/challenges')));
    if (result && typeof result.finally === 'function') {
      result.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 4000);
    }
  }, [state, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete ─────────────────────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this challenge?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Challenge',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteChallenges(state.challenge_id, () => navigate('/challenges')));
      }
    });
  };

  /* ─── Render ─────────────────────────────────────────────── */
  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="challenge-form"
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Challenge name" required htmlFor="f-ch-title">
              <TextInput
                id="f-ch-title"
                name="challenge_title"
                value={state.challenge_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock
              label="Goal"
              required
              hint="Short tagline shown under the title in the app"
              htmlFor="f-ch-goal"
            >
              <TextInput
                id="f-ch-goal"
                name="challenge_goal"
                value={state.challenge_goal}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Description" htmlFor="f-ch-desc">
              <Textarea
                id="f-ch-desc"
                name="challenge_description"
                value={state.challenge_description || ''}
                onChange={handleFieldChange}
                rows={3}
                placeholder="Shown on the challenge landing page."
              />
            </FieldBlock>
            <FieldBlock label="Author" className="max-w-[480px]" noMargin>
              <AuthorSelectButton
                value={state.challenge_author_id}
                onChange={(value) => setField('challenge_author_id', value)}
              />
            </FieldBlock>
          </FormSection>

          {/* 02 Schedule */}
          <FormSection title="Schedule" num="02">
            <FieldRow cols={2} minColWidth={200}>
              <FieldBlock label="Duration" hint="Total number of days or weeks">
                <TextInput
                  type="number"
                  name="total_days"
                  value={state.total_days}
                  onChange={handleFieldChange}
                  min={0}
                />
              </FieldBlock>
              <FieldBlock label="Day type" hint="Cadence unit">
                <Select
                  name="challenge_day_type"
                  value={state.challenge_day_type || ''}
                  onChange={handleFieldChange}
                >
                  <option value="">— pick —</option>
                  <option value="DAY">DAY</option>
                  <option value="WEEK">WEEK</option>
                </Select>
              </FieldBlock>
            </FieldRow>
          </FormSection>

          {/* 03 Intro session */}
          <FormSection title="Intro session" num="03">
            <FieldBlock label="Intro title" required htmlFor="f-intro-title">
              <TextInput
                id="f-intro-title"
                name="session_name"
                value={state.intro_session.session_name}
                onChange={handleIntroFieldChange}
                required
              />
            </FieldBlock>
            <FieldRow cols={2} minColWidth={240}>
              <AudioTile
                label="Intro audio"
                hint="MP3 / M4A · auto-detects duration"
                value={
                  state.intro_session.session_main_url_preview
                  || state.intro_session.session_main_url
                }
                onChange={handleIntroAudio}
                onLoadedMetadata={handleIntroAudioLoaded}
                onRemove={() => {
                  if (state.intro_session.session_main_url_preview) {
                    window.URL.revokeObjectURL(state.intro_session.session_main_url_preview);
                  }
                  setState((prev) => ({
                    ...prev,
                    intro_session: {
                      ...prev.intro_session,
                      session_main_url: '',
                      session_main_url_preview: '',
                      session_length: '',
                      session_length_in_sec: '',
                    },
                  }));
                }}
              />
              <UploadTile
                label="Intro image"
                value={
                  state.intro_session.session_image_preview
                  || state.intro_session.session_image
                }
                accept="image/*"
                onChange={handleIntroImage}
                onRemove={() => {
                  if (state.intro_session.session_image_preview) {
                    window.URL.revokeObjectURL(state.intro_session.session_image_preview);
                  }
                  setState((prev) => ({
                    ...prev,
                    intro_session: {
                      ...prev.intro_session,
                      session_image: '',
                      session_image_preview: '',
                    },
                  }));
                }}
              />
            </FieldRow>
          </FormSection>

          {/* 04 Sharing & Media — main card art */}
          <FormSection title="Sharing & Media" num="04">
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Challenge image"
                hint="Library card art"
                value={state.challenge_image_preview || state.challenge_image}
                accept="image/*"
                onChange={handleMainImage}
                onRemove={() => {
                  if (state.challenge_image_preview) window.URL.revokeObjectURL(state.challenge_image_preview);
                  setState((prev) => ({ ...prev, challenge_image: '', challenge_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Wide image"
                hint="Wide variant for hero / detail header"
                value={state.challenge_image_wide_preview || state.challenge_image_wide}
                accept="image/*"
                onChange={handleWideImage}
                onRemove={() => {
                  if (state.challenge_image_wide_preview) window.URL.revokeObjectURL(state.challenge_image_wide_preview);
                  setState((prev) => ({ ...prev, challenge_image_wide: '', challenge_image_wide_preview: '' }));
                }}
              />
            </FieldRow>
            {isEdit && state.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={state.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* 05 Notifications & certificates */}
          <FormSection title="Notifications & certificates" num="05">
            <FieldRow cols={3} minColWidth={200}>
              <UploadTile
                label="Reminder image"
                hint="Push-notification artwork"
                value={state.challenge_reminder_image_preview || state.challenge_reminder_image}
                accept="image/*"
                onChange={handleReminderImage}
                onRemove={() => {
                  if (state.challenge_reminder_image_preview) window.URL.revokeObjectURL(state.challenge_reminder_image_preview);
                  setState((prev) => ({ ...prev, challenge_reminder_image: '', challenge_reminder_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Certificate image"
                hint="Completion certificate"
                value={state.challenge_certificate_image_preview || state.challenge_certificate_image}
                accept="image/*"
                onChange={handleCertificateImage}
                onRemove={() => {
                  if (state.challenge_certificate_image_preview) window.URL.revokeObjectURL(state.challenge_certificate_image_preview);
                  setState((prev) => ({ ...prev, challenge_certificate_image: '', challenge_certificate_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Invitation image"
                hint="Shared in invite flows"
                value={state.challenge_invite_image_preview || state.challenge_invite_image}
                accept="image/*"
                onChange={handleInviteImage}
                onRemove={() => {
                  if (state.challenge_invite_image_preview) window.URL.revokeObjectURL(state.challenge_invite_image_preview);
                  setState((prev) => ({ ...prev, challenge_invite_image: '', challenge_invite_image_preview: '' }));
                }}
              />
            </FieldRow>
          </FormSection>

          {/* 06 Sessions */}
          {isEdit && (
            <FormSection
              title="Sessions"
              num="06"
              right={
                <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                  {(state.sessions || []).length}
                </span>
              }
            >
              <ChallengeSessionsList
                items={state.sessions || []}
                sessionData={state.session_data || []}
                onSortEnd={handleSessionSort}
                handleChallengesDay={handleChallengesDay}
                emptyText="No sessions attached yet."
              />
            </FormSection>
          )}

          {/* 07 Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="07">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete} type="button">
                  Delete challenge
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardCH state={state} />
          {isEdit && (
            <PublishingCardCH
              state={state}
              sessionCount={(state.sessions || []).length}
              onToggleActive={(next) => setField('challenge_is_active', next ? 1 : 0)}
            />
          )}
          <SettingsCardCH
            state={state}
            onChange={(name, value) => setField(name, value)}
          />
        </aside>
      </div>
    </>
  );
});

export default AddEditChallengeV2;
